import React from "react";
import { Copy, Check, MessageCircle, Twitter, Instagram, Download, RectangleHorizontal, Square, RectangleVertical } from "lucide-react";
import { useLang } from "@/lib/useLang";

const FORMATS = [
  { id: "landscape", label: "Landscape", sub: "1200×630", Icon: RectangleHorizontal, file: "/og-kerala-speaks-landscape.png" },
  { id: "square", label: "Square", sub: "1200×1200", Icon: Square, file: "/og-kerala-speaks-square.png" },
  { id: "story", label: "Story", sub: "1080×1920", Icon: RectangleVertical, file: "/og-kerala-speaks-story.png" },
];

export const ShareLine = () => {
  const { t } = useLang();
  const [copied, setCopied] = React.useState(false);
  const [fmt, setFmt] = React.useState("landscape");
  const shareUrl = typeof window !== "undefined" ? window.location.origin : "https://www.thenextcm.com";
  const shareText = t("share_line_text");
  const fullText = `${shareText} ${shareUrl}`;

  const waUrl = `https://wa.me/?text=${encodeURIComponent(fullText)}`;
  const xUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
  const activeFmt = FORMATS.find((f) => f.id === fmt) || FORMATS[0];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  const handleInstagram = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
    } catch { /* ignore */ }
    try {
      const a = document.createElement("a");
      a.href = "instagram://library?LocalIdentifier=";
      document.body.appendChild(a); a.click(); a.remove();
    } catch { /* ignore */ }
  };

  return (
    <section className="brutal-card p-5 md:p-7" data-testid="share-section">
      <p className="text-xs uppercase font-bold tracking-[0.3em] text-neutral-600">
        {t("share_eyebrow")}
      </p>
      <h2
        className="font-display uppercase leading-none mt-2 mb-4"
        style={{ fontSize: "clamp(1.5rem, 3.5vw, 2.25rem)" }}
        data-testid="share-heading"
      >
        {t("share_heading")}
      </h2>
      <p className="text-sm md:text-base font-medium text-neutral-700 max-w-2xl mb-5">
        {t("share_sub")}
      </p>

      {/* Format picker */}
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="share-format-picker">
        {FORMATS.map(({ id, label, sub, Icon }) => {
          const active = fmt === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setFmt(id)}
              className="inline-flex items-center gap-2 px-3 py-2 border-2 text-[10px] uppercase font-extrabold tracking-[0.2em] transition-colors"
              style={{
                background: active ? "#0A0A0A" : "transparent",
                color: active ? "#FF9933" : "#0A0A0A",
                borderColor: "#0A0A0A",
              }}
              data-testid={`fmt-${id}`}
            >
              <Icon size={14} strokeWidth={3} />
              <span>{label}</span>
              <span className="opacity-60 hidden sm:inline">· {sub}</span>
            </button>
          );
        })}
      </div>

      <div
        className="border-4 border-black mb-3 overflow-hidden bg-[#FDFBF7] mx-auto"
        style={{
          maxWidth: fmt === "story" ? 300 : fmt === "square" ? 420 : 720,
        }}
        data-testid="share-hero-image-wrap"
      >
        <img
          key={activeFmt.id}
          src={`${activeFmt.file}?v=2`}
          alt={`Kerala Speaks — The First 100 Days (${activeFmt.label})`}
          loading="lazy"
          className="block w-full h-auto"
          data-testid="share-hero-image"
        />
      </div>

      <a
        href={`${activeFmt.file}?v=2`}
        download={`kerala-speaks-${activeFmt.id}.png`}
        className="inline-flex items-center gap-2 mb-5 text-xs uppercase font-extrabold tracking-[0.25em] underline-offset-4 hover:underline"
        data-testid="share-hero-download"
      >
        <Download size={14} strokeWidth={3} />
        Download {activeFmt.label.toLowerCase()} ({activeFmt.sub})
      </a>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <a
          className="brutal-btn"
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ background: "#138808", borderColor: "#0A0A0A", color: "#FFFFFF" }}
          data-testid="share-whatsapp-btn"
        >
          <MessageCircle size={18} strokeWidth={3} className="mr-2" /> {t("whatsapp")}
        </a>
        <a
          className="brutal-btn"
          href={xUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="share-twitter-btn"
        >
          <Twitter size={18} strokeWidth={3} className="mr-2" /> {t("post_x")}
        </a>
        <button
          type="button"
          className="brutal-btn"
          onClick={handleInstagram}
          style={{
            background: "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
            color: "#FFFFFF",
            borderColor: "#0A0A0A",
          }}
          data-testid="share-instagram-btn"
        >
          <Instagram size={18} strokeWidth={3} className="mr-2" /> {t("instagram")}
        </button>
        <button
          type="button"
          className="brutal-btn"
          onClick={handleCopy}
          style={copied ? { background: "#FF9933", color: "#0A0A0A" } : undefined}
          data-testid="share-copy-btn"
        >
          {copied ? <Check size={18} strokeWidth={3} className="mr-2" /> : <Copy size={18} strokeWidth={3} className="mr-2" />}
          {copied ? t("copied") : t("copy_link")}
        </button>
      </div>
    </section>
  );
};

export default ShareLine;
